/**
 * The piece's thumbnail, on the posting card (Aziz 2026-10-30): once the clinic approves,
 * the person posting it adds the thumbnail, uploaded or made from the clinic's brand kit
 * (a cover template, the headline starting as the piece's title). A video or reel cannot
 * be marked posted without one; the server holds the same line.
 */
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Palette, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import Generator from '@/components/brand/Generator';
import { fetchBrandKit, fetchVisibleTemplates, templateEntries } from '@/lib/brand-kit';
import { fetchAssets, qk, type Creative } from '@/lib/queries';
import { uploadAsset } from '@/lib/upload';

export const needsThumbnail = (creativeType?: string | null) => !creativeType || creativeType === 'video' || creativeType === 'reel';

function BrandKitMaker({ creative, onClose, onSaved }: { creative: Creative; onClose: () => void; onSaved: () => void }) {
  const kit = useQuery({ queryKey: ['brand-kit', creative.client_id], queryFn: () => fetchBrandKit(creative.client_id) });
  const vis = useQuery({ queryKey: qk.brandTemplates(creative.client_id), queryFn: () => fetchVisibleTemplates(creative.client_id) });
  return (
    <div role="dialog" aria-modal="true" aria-label="Make the thumbnail from the brand kit"
      className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm overflow-y-auto p-4" data-testid="thumb-maker">
      <div className="max-w-6xl mx-auto bg-bg rounded-card border border-slate-800 p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-muted-strong">Thumbnail for {creative.code}</h3>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-lg text-muted hover:text-muted-strong hover:bg-bg-card"><X size={18} /></button>
        </div>
        {kit.isLoading || vis.isLoading ? <p className="text-sm text-muted">Loading the brand kit...</p>
          : !kit.data || !vis.data ? <p className="text-sm text-red-400">Could not load this clinic's brand kit.</p>
          : <Generator clientId={creative.client_id} kit={kit.data.kit} people={kit.data.people} templates={templateEntries(vis.data)}
              canManage={false} canSave={false} forCreative={{ id: creative.id, code: creative.code, title: creative.title }}
              onSavedToPiece={() => { onSaved(); onClose(); }} />}
      </div>
    </div>
  );
}

export default function ThumbnailSlot({ creative }: { creative: Creative }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [making, setMaking] = useState(false);
  const thumbs = useQuery({ queryKey: ['assets', creative.id, 'thumbnail'], queryFn: () => fetchAssets(creative.id, 'thumbnail') });
  const current = (thumbs.data ?? []).find((a) => a.active !== false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['assets', creative.id] });
    void qc.invalidateQueries({ queryKey: ['creative', creative.id] });
  };
  const upload = async (f?: File) => {
    if (!f) return;
    setBusy(true);
    try { await uploadAsset({ creativeId: creative.id, file: f, kind: 'thumbnail' }); toast.success('Thumbnail added'); refresh(); }
    catch (e) { toast.error((e as Error).message || 'Could not upload'); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  const required = needsThumbnail(creative.creative_type);
  return (
    <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-emerald-500/20" data-testid="thumb-slot">
      <div className="h-24 w-[54px] shrink-0 rounded-md border border-line bg-bg overflow-hidden flex items-center justify-center">
        {current ? <img src={current.thumbnail_url || current.download_url || undefined} alt="Thumbnail" className="h-full w-full object-cover" data-testid="thumb-image" />
          : <ImageIcon size={18} className="text-muted" />}
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm text-muted-strong">{current ? 'Thumbnail ready' : 'Thumbnail'}</div>
        <div className="text-xs text-muted">{current ? 'Replace it any time before posting.'
          : required ? 'Needed before this can be marked posted.' : 'Optional for this kind of piece.'}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" icon={<Upload size={12} />} loading={busy} onClick={() => input.current?.click()} data-testid="thumb-upload">
          {current ? 'Upload another' : 'Upload thumbnail'}</Button>
        <Button size="sm" variant="secondary" icon={<Palette size={12} />} onClick={() => setMaking(true)} data-testid="thumb-make">Make from brand kit</Button>
      </div>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
      {making && <BrandKitMaker creative={creative} onClose={() => setMaking(false)} onSaved={refresh} />}
    </div>
  );
}
